import React from 'react';
import { Link } from 'react-router-dom';
import { WorkspaceApiClient } from '@/infrastructure/workspace/WorkspaceApiClient';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

export default function ChildrenManagementPage({ session }) {
  const { t } = useAppTranslation();
  const api = React.useMemo(() => new WorkspaceApiClient(), []);
  const token = session?.accessToken || null;
  const isStudent = session?.role === 'student';
  const isParent = session?.role === 'parent';
  const [children, setChildren] = React.useState([]);
  const [parents, setParents] = React.useState([]);
  const [parentId, setParentId] = React.useState('');
  const [relation, setRelation] = React.useState('parent');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [result, setResult] = React.useState('');
  const [profile, setProfile] = React.useState({
    age: '',
    school: '',
    phone: '',
    address: '',
    parent_info: '',
  });

  const load = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      if (isParent) {
        const x = await api.listChildren(token);
        setChildren(Array.isArray(x?.items) ? x.items : []);
      }
      if (isStudent) {
        const x = await api.listMyParents(token);
        setParents(Array.isArray(x?.items) ? x.items : []);
        const p = await api.getMyStudentProfile(token);
        setProfile({
          age: p?.age ?? '',
          school: p?.school ?? '',
          phone: p?.phone ?? '',
          address: p?.address ?? '',
          parent_info: p?.parent_info ?? '',
        });
      }
    } catch (e) {
      setError(formatApiErrorMessage(e?.message, t) || t('errors.loadChildren'));
    } finally {
      setLoading(false);
    }
  }, [api, isParent, isStudent, token, t]);

  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <h1 className="font-headline text-3xl font-extrabold">
        {isParent ? t('children.titleParent') : t('children.titleStudent')}
      </h1>
      {loading ? <p className="text-sm text-slate-600">{t('children.loading')}</p> : null}
      {error ? <p className="text-sm font-semibold text-rose-700">{error}</p> : null}

      {isParent ? (
        <section className="space-y-2">
          {children.map((c) => (
            <article key={c.id} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
              <p className="font-semibold text-slate-900">{c.name}</p>
              <p className="text-xs text-slate-500">{c.email || 'N/A'}</p>
              <p className="mt-1 text-xs text-slate-500">{t('children.relation', { value: c.relation || 'N/A' })}</p>
              <Link
                to={`/children/${encodeURIComponent(c.id)}`}
                className="mt-2 inline-flex rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-cyan-500 hover:text-cyan-700"
              >
                {t('children.viewLearning')}
              </Link>
            </article>
          ))}
          {!loading && children.length === 0 ? (
            <p className="rounded-xl border border-slate-100 bg-white p-4 text-sm text-slate-600">{t('children.noChildren')}</p>
          ) : null}
        </section>
      ) : null}

      {isStudent ? (
        <>
          <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-bold">{t('children.linkTitle')}</h2>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <input
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder={t('children.parentIdPlaceholder')}
              />
              <input
                value={relation}
                onChange={(e) => setRelation(e.target.value)}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder={t('children.relationPlaceholder')}
              />
              <button
                type="button"
                onClick={async () => {
                  if (!parentId.trim()) return;
                  try {
                    const v = parentId.trim();
                    const data = await api.addMyParent(
                      v,
                      relation.trim() || 'parent',
                      token,
                      v.includes('@')
                        ? { parent_email: v, relation: relation.trim() || 'parent' }
                        : { parent_id: v, relation: relation.trim() || 'parent' },
                    );
                    setResult(data?.already_linked ? t('children.alreadyLinked') : t('children.linkedOk'));
                    setParentId('');
                    await load();
                  } catch (e) {
                    setResult(formatApiErrorMessage(e?.message, t) || t('children.linkFail'));
                  }
                }}
                className="rounded-xl bg-cyan-600 px-4 py-2 text-sm font-bold text-white"
              >
                {t('children.addParent')}
              </button>
            </div>
            {result ? <p className="mt-2 text-sm font-semibold text-emerald-700">{result}</p> : null}
          </section>

          <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-bold">{t('children.profileTitle')}</h2>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <input
                value={profile.age}
                onChange={(e) => setProfile((prev) => ({ ...prev, age: e.target.value }))}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder={t('children.agePh')}
              />
              <input
                value={profile.school}
                onChange={(e) => setProfile((prev) => ({ ...prev, school: e.target.value }))}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder={t('children.schoolPh')}
              />
              <input
                value={profile.phone}
                onChange={(e) => setProfile((prev) => ({ ...prev, phone: e.target.value }))}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder={t('children.phonePh')}
              />
              <input
                value={profile.address}
                onChange={(e) => setProfile((prev) => ({ ...prev, address: e.target.value }))}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder={t('children.addressPh')}
              />
              <input
                value={profile.parent_info}
                onChange={(e) => setProfile((prev) => ({ ...prev, parent_info: e.target.value }))}
                className="md:col-span-2 rounded-xl border border-slate-200 px-3 py-2 text-sm"
                placeholder={t('children.parentInfoPh')}
              />
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  await api.updateMyStudentProfile(
                    {
                      age: profile.age === '' ? null : Number(profile.age),
                      school: profile.school,
                      phone: profile.phone,
                      address: profile.address,
                      parent_info: profile.parent_info,
                    },
                    token,
                  );
                  setResult(t('children.saveOk'));
                } catch (e) {
                  setResult(formatApiErrorMessage(e?.message, t) || t('children.saveFail'));
                }
              }}
              className="mt-3 rounded-xl border border-cyan-300 px-4 py-2 text-sm font-bold text-cyan-700"
            >
              {t('children.saveProfile')}
            </button>
          </section>

          <section className="space-y-2">
            {parents.map((p) => (
              <article key={p.id} className="flex items-center justify-between rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <div>
                  <p className="font-semibold text-slate-900">{p.name}</p>
                  <p className="text-xs text-slate-500">{p.email || 'N/A'}</p>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    await api.removeMyParent(p.id, token);
                    await load();
                  }}
                  className="rounded-lg border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-700"
                >
                  {t('children.removeLink')}
                </button>
              </article>
            ))}
            {!loading && parents.length === 0 ? (
              <p className="rounded-xl border border-slate-100 bg-white p-4 text-sm text-slate-600">{t('children.noParents')}</p>
            ) : null}
          </section>
        </>
      ) : null}
    </div>
  );
}
